import { useMutation, useQuery } from "@apollo/client";
import { Empty, message } from "antd";
import { useState } from "react";
import {
  CasesIdentityVerificationV1CaseDetailsApplicationContainerIdentityVerificationCaseV1Document,
  CasesIdentityVerificationV1CaseDetailsApplicationContainerIdentityVerificationCaseV1UpdateDocument,
  IdentityVerificationCaseV1,
  IdentityVerificationCaseV1CaseDetailsApplicationPassportDetails,
  IdentityVerificationCaseV1RevisionRequest,
  IdentityVerificationCaseV1UpdateInput,
} from "../../../../../../../../generated";
import { lookupCountryQuery } from "./case-details-country-query";
import { Country } from "../../../../../../../../types/country";
import { ComponentQueryLoader } from "../../../../../../../shared/component-query-loader";
import { AzureBlobResponse } from "../../../../../../../shared/file-upload/azure-upload";
import { CaseDetailsApplication } from "./case-details-application";

interface CaseDetailsApplicationContainerProps {
  caseId: string;
}

export const CaseDetailsApplicationContainer: React.FC<CaseDetailsApplicationContainerProps> = (props) => {
  const {
    data: caseData,
    loading: caseDataLoading,
    error: caseDataError,
  } = useQuery(CasesIdentityVerificationV1CaseDetailsApplicationContainerIdentityVerificationCaseV1Document, {
    variables: {
      identityVerificationCaseV1Id: props.caseId,
    },
  });
  const {
    data: countryData,
    error: countryError,
    loading: countryDataLoading,
  } = useQuery(lookupCountryQuery);

  const [accumulatedUpdateInput, setAccumulatedUpdateInput] = useState({
    id: props.caseId,
    revisionRequest: {
      requestedChanges: {
        ...caseData?.identityVerificationCaseV1?.revisionRequest?.requestedChanges,
      },
    },
  } as IdentityVerificationCaseV1UpdateInput);

  // for upload file: start
  const [saveApplication, { loading: isSavingApplication }] = useMutation(
    CasesIdentityVerificationV1CaseDetailsApplicationContainerIdentityVerificationCaseV1UpdateDocument,
    {
      refetchQueries: [
        {
          query: CasesIdentityVerificationV1CaseDetailsApplicationContainerIdentityVerificationCaseV1Document,
          variables: {
            identityVerificationCaseV1Id: props.caseId,
          },
        },
      ],
      awaitRefetchQueries: true,
    }
  );

  const handleOnUploadSuccess = async (uploadedData: AzureBlobResponse, fieldName: string) => {
    message.success("File uploaded successfully");
    const assetName = fieldName === "photo" ? "photoAssets" : "passportAssets";
    const { blobUrl, versionId } = uploadedData;

    const input: IdentityVerificationCaseV1UpdateInput = {
      ...accumulatedUpdateInput,
      caseDetails: {
        application: {
          ...accumulatedUpdateInput.caseDetails?.application,
          [assetName]: {
            [`${fieldName}`]: blobUrl,
            [`${fieldName}Version`]: versionId,
          },
        },
      },
    };

    if(fieldName === "passportExpiration"){
      if(input.caseDetails){
        input.caseDetails.application = {
          ...input.caseDetails.application,
          passportDetails:{
            isPassportExpirationDateVisible: false
          }
        }
      }
    }

    if(fieldName === "passportTranslation"){
      if(input.caseDetails){
        input.caseDetails.application = {
          ...input.caseDetails.application,
          passportDetails:{
            isPassportInLatinCharacters: false
          }
        }
      }
    }
    setAccumulatedUpdateInput(input);
  };

  // for upload file: end

  const handleSave = async (updatedPassportDetails: IdentityVerificationCaseV1CaseDetailsApplicationPassportDetails) => {
    const updateInput: IdentityVerificationCaseV1UpdateInput = {
      id: props.caseId,
      caseDetails: {
        application: {
          ...accumulatedUpdateInput.caseDetails?.application,
          passportDetails: {
            ...updatedPassportDetails,
          },
        },
      },
    };

    try {
      const response = await saveApplication({
        variables: {
          input: updateInput,
        },
      });

      if (response.data?.identityVerificationCaseV1Update.status.success) {
        message.success("Application data saved successfully");
      } else {
        message.error("Error saving Application data: " + response.data?.identityVerificationCaseV1Update.status.errorMessage);
        return Promise.reject();
      }
    } catch (error) {
      message.error("Error saving Application data");
      return Promise.reject();
    }
  };

  const handleSaveAndComplete = async (
    updatedPassportDetails: IdentityVerificationCaseV1CaseDetailsApplicationPassportDetails,
    revisionRequest: IdentityVerificationCaseV1RevisionRequest
  ) => {
    const updateInput = {
      id: props.caseId,
      caseDetails: {
        application: {
          ...accumulatedUpdateInput.caseDetails?.application,
          passportDetails: {
            ...updatedPassportDetails,
            ...accumulatedUpdateInput.caseDetails?.application?.passportDetails,
          },
        },
      },
      revisionRequest: {
        requestedChanges: {
          ...revisionRequest.requestedChanges,
        },
        revisionSubmittedAt: revisionRequest.revisionSubmittedAt,
      },
    };
    try {
      const response = await saveApplication({
        variables: {
          input: updateInput,
        },
      });

      if (response.data?.identityVerificationCaseV1Update.status.success) {
        message.success("Application data saved successfully");
      } else {
        message.error("Error saving Application data");
      }
    } catch (error) {
      message.error("Error saving Application data");
    }
  };
  return (
    <ComponentQueryLoader
      loading={caseDataLoading || countryDataLoading}
      error={caseDataError || countryError}
      hasData={caseData && countryData}
      hasDataComponent={
        <CaseDetailsApplication
          data={caseData?.identityVerificationCaseV1 as IdentityVerificationCaseV1}
          onSave={handleSave}
          onSaveAndComplete={handleSaveAndComplete}
          caseId={props.caseId}
          countryData={countryData?.countries?.eventPayload?.countries as Country[]}
          onUploadSuccess={handleOnUploadSuccess}
          isSavingApplication={isSavingApplication}
        />
      }
      errorComponent={<>{<Empty description={`There was an error loading the application data. Please try again!`} />}</>}
    />
  );
};
